import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import { makeStyles } from '@material-ui/core/styles';

import { deleteNotification } from '../actions/notifications';

import { getIsNotificationsTry } from '../reducers';

import { efficientSetInterval, getMomentFromTimestamp } from '../utils';
import SnackBarControl from './SnackBarControl/SnackBarControl';
import { useState } from 'react';
import storage from '../storage';
import { NOTIFICATIONS_POSITION } from '../constants/cookies';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    top: theme.spacing(7),
    left: 'auto',
    right: 0,
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(2),
    maxHeight: `calc(100vh - ${theme.spacing(7)}px)`,
    fontSize: '0.9rem',
    zIndex: '1000',

    '&.position': {
      '&__right': {
        left: 'auto',
        right: 0,
        paddingRight: theme.spacing(3),
      },
      '&__left': {
        left: 0,
        right: 'auto',
        paddingLeft: theme.spacing(3),
      },
    },

    '&:empty': {
      display: 'none',
    },

    '& .MuiAlert-filledSuccess': {
      backgroundColor: '#13a252',
    },

    '& .MuiAlert-filledError': {
      backgroundColor: '#fa4c4c',
    },

    [theme.breakpoints.up('md')]: {
      top: theme.spacing(8),
    },

    '& .MuiSnackbar-root': {
      position: 'initial',
      marginBottom: theme.spacing(2),

      [theme.breakpoints.up('md')]: {
        top: theme.spacing(8),
      },

      // orderStatusNotification
      '&.orderStatusNotification': {
        '& .error_message': {
          margin: '3px 0',
          wordBreak: 'break-word',
        },
        '& .MuiAlert-root': {
          fontSize: '0.79rem',
          position: 'relative',

          [theme.breakpoints.up('md')]: {
            width: 200,
          },
        },
        '& .MuiAlert-icon': {
          top: 10,
          left: 12,
          position: 'absolute',
        },

        '& .MuiAlertTitle-root': {
          fontSize: '0.9rem',
          paddingLeft: 24,
          paddingRight: 14,
        },

        '& .MuiAlert-action': {
          position: 'absolute',
          top: 10,
          right: 18,
          padding: '0',

          '& .MuiIconButton-sizeSmall': {
            padding: '0',
          },
        },
      },
    },

    '& .MuiAlert-root': {
      alignItems: 'center',
      padding: '10px 12px',
      paddingRight: '16px',
      maxWidth: 250,
      fontWeight: '400',

      [theme.breakpoints.up('md')]: {
        maxWidth: 500,
      },
    },

    '& .MuiAlert-message': {
      margin: 0,
      padding: 0,
      wordBreak: 'break-word',
    },

    '& .MuiSvgIcon-root': {
      width: '17px',
      height: '17px',
    },

    '& .MuiAlert-icon': {
      padding: '0',
      marginRight: 10,
    },

    '& .timestamp': {
      margin: 0,
      fontSize: '0.8rem',
    },
  },
}));

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

function OrderStatusNotification({
  id,
  onNotificationClose,
  severity,
  orderStatus,
  position,
  timestamp,
  type,
}) {
  return (
    <Snackbar
      open={true}
      anchorOrigin={{ vertical: 'top', horizontal: position }}
      className="orderStatusNotification"
    >
      <Alert severity={severity} onClose={() => onNotificationClose(id)}>
        <AlertTitle>{orderStatus.status.toUpperCase()}</AlertTitle>

        <div>{orderStatus.symbol}</div>

        {orderStatus.status === 'complete' && orderStatus.transaction_type ? (
          <div className="transaction_type">
            {orderStatus.transaction_type === 'B' ? 'BUY' : 'SELL'} @{' '}
            {orderStatus.average_price} {`(${orderStatus.quantity})`}
          </div>
        ) : (
          ''
        )}

        {orderStatus.status === 'rejected' ? (
          // eslint-disable-next-line react/no-unescaped-entities
          <div className="error_message">"{orderStatus.message}"</div>
        ) : (
          ''
        )}

        {orderStatus.client_id ? (
          <div className="client_id">Client ID: {orderStatus.client_id}</div>
        ) : (
          ''
        )}

        {orderStatus.order_id ? (
          <div className="order_id">Order ID: {orderStatus.order_id}</div>
        ) : (
          ''
        )}

        {type === 'error' && timestamp ? (
          <div className="timestamp">
            {getMomentFromTimestamp(timestamp).format('HH:mm:ss')}
          </div>
        ) : (
          ''
        )}
      </Alert>
    </Snackbar>
  );
}

function Notification({
  id,
  message,
  source = '',
  type,
  orderUpdate = false,
  onNotificationClose,
  orderStatus = {},
  position,
  timestamp,
}) {
  const severity = {
    error: 'error',
    success: 'success',
    info: 'info',
  };
  return (
    <>
      {orderUpdate ? (
        <OrderStatusNotification
          id={id}
          onNotificationClose={onNotificationClose}
          severity={severity[type] ? severity[type] : 'info'}
          orderStatus={orderStatus}
          position={position}
          timestamp={timestamp}
          type={type}
        />
      ) : (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          anchorOrigin={{ vertical: 'top', horizontal: position }}
          key={id}
        >
          <Alert
            severity={severity[type] ? severity[type] : 'info'}
            onClose={() => onNotificationClose(id)}
          >
            {source && source.trim() !== '' ? `${source} - ` : null}
            {message}
            {type === 'error' && timestamp ? (
              <span className="timestamp">
                {' '}
                {getMomentFromTimestamp(timestamp).format('HH:mm:ss')}
              </span>
            ) : (
              ''
            )}
          </Alert>
        </Snackbar>
      )}
    </>
  );
}

function SnackBox({ notifications, onNotificationClose, maxSnack }) {
  const classes = useStyles();
  const [position, setPosition] = useState(
    storage.getItem(NOTIFICATIONS_POSITION) || 'right'
  );

  let notificationsTotal = notifications;

  if (maxSnack && maxSnack > 0) {
    notificationsTotal = notificationsTotal.slice(0, maxSnack + 1);
  }

  const snackbarPositionToggle = () => {
    const newPosition = position === 'left' ? 'right' : 'left';
    setPosition(newPosition);

    storage.setItems({ [NOTIFICATIONS_POSITION]: newPosition });
  };

  return (
    <div className={`${classes.root} position__${position}`}>
      <SnackBarControl
        snackbarPositionToggle={snackbarPositionToggle}
        position={position}
      />
      {notificationsTotal.map((notification) => (
        <Notification
          key={notification.id}
          onNotificationClose={onNotificationClose}
          position={position}
          {...notification}
        />
      ))}
    </div>
  );
}

SnackBox.propTypes = {
  loading: PropTypes.bool.isRequired,
};

class SnackBoxBase extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
    };

    this.handleCloseNotifications = this.handleCloseNotifications.bind(this);
  }

  handleCloseNotifications(id) {
    this.props.deleteNotification(id);
  }

  componentDidMount() {
    efficientSetInterval(
      () => {
        const { data: notifications = [] } = this.props.notifications;
        if (notifications.length) {
          const timeNow = moment
            .utc()
            .subtract(this.props.autoHideDurationInSeconds, 'seconds');
          notifications.forEach(({ timestamp, id }) => {
            if (moment(timestamp).isBefore(timeNow)) {
              this.props.deleteNotification(id);
            }
          });
        }
      },
      3000,
      { leading: true }
    );
  }

  render() {
    return (
      <SnackBox
        {...this.state}
        {...this.props}
        notifications={
          this.props.notifications.data && this.props.notifications.data.length
            ? this.props.notifications.data
            : []
        }
        onNotificationClose={this.handleCloseNotifications}
      />
    );
  }
}

SnackBoxBase.defaultProps = {
  maxSnack: 0,
  autoHideDurationInSeconds: 10,
};

SnackBoxBase.propTypes = {
  maxSnack: PropTypes.number,
  autoHideDurationInSeconds: PropTypes.number,
};

const mapStateToProps = (state) => {
  return {
    notifications: getIsNotificationsTry(state),
  };
};

export default connect(mapStateToProps, { deleteNotification })(SnackBoxBase);
